import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';

@Component({
    selector: 'app-rotator',
    templateUrl: './rotator.component.html',
    styleUrls: ['./rotator.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => RotatorComponent),
        }
    ],
    standalone: false
})
export class RotatorComponent implements ControlValueAccessor, OnInit, OnDestroy {

    @Input() set value(_value) {
        this.rotated = _value || 0;
        this.updateProgress();
    }

    @Input() set disabled(_status) {
        this.status = !!_status;
    }

    @Input() min = 0;
    @Input() max = 359;
    @Input() step = 1;
    @Input() showToolTop = true;

    @Output() changed: EventEmitter<any> = new EventEmitter();

    private subSelection = new Subject<any>();
    public status = false;
    public rotated = 0;
    public progressOffset = 220; // For SVG circle progress (circumference - progress)

    private isDragging = false;
    private centerX = 0;
    private centerY = 0;

    // ControlValueAccessor properties
    private propagateChange = (_: any) => { };
    private propagateTouched = () => { };

    constructor() {
        this.subSelection.pipe(
            debounceTime(100)
        ).subscribe(event => {
            this.changed.emit(event);
            this.propagateChange(event);
        });
    }

    ngOnInit(): void {
        this.updateProgress();
    }

    ngOnDestroy() {
        if (this.subSelection) {
            this.subSelection.unsubscribe();
        }
    }

    onMouseDown(event: MouseEvent) {
        if (this.status) return;

        event.preventDefault();
        this.isDragging = true;

        const svg = event.currentTarget as SVGSVGElement;
        const rect = svg.getBoundingClientRect();
        this.centerX = rect.left + rect.width / 2;
        this.centerY = rect.top + rect.height / 2;

        this.updateRotation(event);
    }

    @HostListener('document:mousemove', ['$event'])
    onMouseMove(event: MouseEvent) {
        if (!this.isDragging) return;
        this.updateRotation(event);
    }

    @HostListener('document:mouseup')
    onMouseUp() {
        this.isDragging = false;
    }

    private updateRotation(event: MouseEvent) {
        const dx = event.clientX - this.centerX;
        const dy = event.clientY - this.centerY;

        // Calculate angle in degrees (0-359)
        let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
        if (angle < 0) angle += 360;

        // Apply step
        angle = Math.round(angle / this.step) * this.step;

        // Clamp to min/max
        angle = Math.max(this.min, Math.min(this.max, angle));

        if (angle !== this.rotated) {
            this.rotated = angle;
            this.updateProgress();
            this.subSelection.next(this.rotated);
        }
    }

    private updateProgress() {
        // SVG circle circumference = 2 * PI * radius = 2 * 3.14159 * 35 ≈ 220
        const circumference = 220;
        const progress = (this.rotated / 360) * circumference;
        this.progressOffset = circumference - progress;
    }

    resetRotation() {
        if (this.status || this.rotated === 0) return;
        this.rotated = 0;
        this.updateProgress();
        this.changed.emit(this.rotated);
        this.propagateChange(this.rotated);
    }

    // ControlValueAccessor implementation
    writeValue(value: any): void {
        if (value !== undefined && value !== null) {
            this.rotated = value;
            this.updateProgress();
        }
    }

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.propagateTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.status = isDisabled;
    }
}
